import React, {
  ReactNode,
  useCallback,
  useEffect,
  useId,
  useState,
} from "react";
import { useNavigate } from "react-router";

import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  CircularProgress,
  Collapse,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";

import { get } from "lodash";

import { ResourceError } from "@features/errors";
import { useFilterParams } from "@features/filters";
import { cn } from "@utils/tailwind";
import { useBottomScrollListener } from "@utils/useBottomScrollListener";

import "./SmartTable.css";

export type Column<R> = {
  id: string;
  label: ReactNode;
  align?: "left" | "right" | "center";
  sort?: string;
  // If true, the sort will be reversed
  // Recommended to use with numeric and date columns
  reverseSort?: boolean;
  className?: string;
  render?: (cell: any, row: R, index: number) => ReactNode;
  renderText?: (cell: any, row: R) => string;
  to?: (row: R) => string;
};

export type PaginatedResourceProps = {
  isLoading?: boolean;
  isFetching?: boolean;
  hasNextPage?: boolean;
  isPlaceholderData?: boolean;
  error?: any;
  fetchNextPage?: () => void;
};

export type SmartTableProps<R> = PaginatedResourceProps & {
  rows: R[];
  columns: Column<R>[];
  onRowClick?: (row: R) => void;
  noResultsText?: string;
  filterState?: Record<string, any>;
  setFilterState?: (x: Record<string, any>) => void;
  className?: string;
  maxHeight?: number;
  tableRef?: React.MutableRefObject<any>;
  rowCss?: (row: R) => string | false | null | undefined;
  collapsibleContent?: (row: R) => ReactNode;
  getCollapsibleRowLabel?: (row: R) => string;
};

const splitSort = (str: string): ["asc" | "desc", string] =>
  str.startsWith("-") ? ["desc", str.slice(1)] : ["asc", str];

const sortDir = (dir: "asc" | "desc", reverse: boolean | undefined) =>
  reverse ? (dir === "asc" ? "desc" : "asc") : dir;

const TableHeader = ({
  columns,
  filterValues,
  setFilterValues,
  hasCollapsibleContent,
}) => {
  let { sort: sortParam } = filterValues;
  const [sortDirection, sortValue] = splitSort(sortParam ?? "");
  const handleSort = (value, reverse) => {
    if (sortValue === value) {
      const switchSortDirection = sortDirection === "desc" ? "" : "-";
      sortParam = switchSortDirection + value;
    } else {
      sortParam = reverse ? "-" + value : value;
    }
    setFilterValues({ ...filterValues, sort: sortParam });
  };

  return (
    <TableHead className="smart-table-head">
      <TableRow>
        {hasCollapsibleContent && <TableCell />}
        {columns.map(({ id, align, label, sort, className, reverseSort }) => (
          <TableCell key={id} align={align} className={className}>
            {sort ? (
              <TableSortLabel
                active={sort === sortValue}
                direction={
                  (sort === sortValue &&
                    sortDir(sortDirection, !reverseSort)) ||
                  "desc"
                }
                onClick={() => handleSort(sort, reverseSort)}
                hideSortIcon={false}
                className="w-full -mr-4"
              >
                {label}
              </TableSortLabel>
            ) : (
              label
            )}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
};

const SmartTableRow = ({
  columns,
  onRowClick,
  collapsibleContent,
  getCollapsibleRowLabel,
  row,
  rowCss,
  rowIndex,
}) => {
  const [open, setOpen] = useState(false);
  const [renderContent, setRenderContent] = useState(false);
  const collapseId = useId();
  const navigate = useNavigate();
  const collapsibleRowLabel =
    getCollapsibleRowLabel?.(row) ?? `row ${rowIndex + 1}`;

  const handleNavigate = (url: string) => (e) => {
    if (e.ctrlKey || e.metaKey) {
      window.open(url, "_blank");
    } else {
      navigate(url, { state: { query: window.location.search } });
    }
  };
  return (
    <>
      <TableRow
        className={cn(
          "hover:bg-neutral-50",
          onRowClick && "[&>:not(.exclude-row-click)]:cursor-pointer",
          rowCss?.(row)
        )}
        onClick={(e) => {
          (e.target as Element).closest(".exclude-row-click") ||
            onRowClick?.(row);
        }}
      >
        {collapsibleContent && (
          <TableCell className="align-middle">
            <IconButton
              size="small"
              edge="end"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((prev) => !prev);
                if (!renderContent) setRenderContent(true);
              }}
              aria-controls={collapseId}
              aria-expanded={open}
              aria-label={`${open ? "Collapse" : "Expand"} details for ${collapsibleRowLabel}`}
              className="text-neutral-500 -my-3"
            >
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          </TableCell>
        )}
        {columns.map((col) => (
          <TableCell
            key={col.id}
            align={col.align ?? "left"}
            onClick={col.to && handleNavigate(col.to?.(row))}
            className={cn(col.className, col.to && "cursor-pointer")}
          >
            {col.render
              ? col.render(get(row, col.id), row, rowIndex)
              : get(row, col.id)}
          </TableCell>
        ))}
      </TableRow>
      {collapsibleContent && renderContent && (
        <TableRow className="box-content">
          <TableCell
            colSpan={columns.length + 1}
            className="p-0 border-0 relative -z-0"
          >
            <Collapse
              in={open}
              timeout="auto"
              className="overflow-hidden w-full"
            >
              <div
                id={collapseId}
                className="bg-neutral-100 py-4 px-6 shadow-inner border-b"
              >
                {collapsibleContent(row)}
              </div>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const SmartTable = <R,>({
  rows,
  isLoading,
  isFetching,
  isPlaceholderData,
  hasNextPage,
  error,
  fetchNextPage,
  columns,
  onRowClick,
  noResultsText = "No results match your search...",
  className,
  filterState,
  setFilterState,
  maxHeight,
  tableRef,
  rowCss,
  collapsibleContent,
  getCollapsibleRowLabel,
}: SmartTableProps<R>) => {
  const [height, setHeight] = useState(0);

  // this is the default filter state
  const [filterParams, setFilterParams] = useFilterParams();

  const filterValues = filterState || filterParams;
  const setFilterValues = setFilterState || setFilterParams;

  const fetchNext = () =>
    !isLoading && !isFetching && hasNextPage && fetchNextPage?.();

  const scrollRef = useBottomScrollListener(fetchNext, {
    offset: 300,
    triggerOnNoScroll: true,
  });

  const heightRef = useCallback(
    (node: HTMLElement | null) => {
      if (tableRef) tableRef.current = node;
      if (maxHeight && node !== null) {
        const nodeHeight = node.getBoundingClientRect().height;
        setHeight(Math.min(nodeHeight, maxHeight));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [maxHeight, tableRef, rows]
  );

  useEffect(() => {
    if (isLoading || !scrollRef.current) return;
    scrollRef.current.scrollTop = 0;
  }, [isLoading, scrollRef]);

  if (isLoading && rows.length === 0)
    return <CircularProgress className="m-6" />;

  return (
    <div className={cn("relative h-full overflow-hidden", className)}>
      <TableContainer
        className={cn(
          "max-h-full transition-opacity",
          isPlaceholderData && "opacity-50 pointer-events-none",
          error && "min-h-[200px]"
        )}
        style={maxHeight ? { height: `${height}px` } : undefined}
        ref={scrollRef}
      >
        <Table stickyHeader className="[&_td]:align-top" ref={heightRef}>
          <TableHeader
            columns={columns}
            filterValues={filterValues}
            setFilterValues={setFilterValues}
            hasCollapsibleContent={Boolean(collapsibleContent)}
          />
          <TableBody>
            {!isLoading && rows.length === 0 && (
              <TableRow key="no-results">
                <TableCell
                  colSpan={columns.length + (collapsibleContent ? 1 : 0)}
                >
                  <Typography>{noResultsText}</Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row: any, i) => (
              <SmartTableRow
                key={`${row.id}-${i}`}
                columns={columns}
                onRowClick={onRowClick}
                collapsibleContent={collapsibleContent}
                getCollapsibleRowLabel={getCollapsibleRowLabel}
                row={row}
                rowCss={rowCss}
                rowIndex={i}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="h-1 w-full absolute left-0 bottom-0">
        {isFetching && !isLoading && <LinearProgress />}
      </div>
      {error && (isPlaceholderData || rows.length === 0) && (
        <div className="absolute inset-0 bg-white/50 backdrop-blur-sm p-4 pt-16">
          <ResourceError error={error} />
        </div>
      )}
    </div>
  );
};

export default SmartTable;
